"use client";

import { useState } from "react";
import { Button, IntentBadge, SettingsSection } from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  modelsHealth,
  type HealthState,
  type ProfileHealth,
} from "@excellent/ui/api/contracts/models-health";

// Gateway-profile health chips (docs/models/PRD.md §8.5.2 / §14): each bridge
// profile shows its doctor state + reasons, with a "Run checkup" that re-runs
// the preflight probes. Quarantine/fallback stay machine-owned — this surface
// only reads and re-probes.

const INTENT: Record<HealthState, "success" | "warning" | "danger" | "neutral"> = {
  healthy: "success",
  degraded: "warning",
  quarantined: "danger",
  unverified: "neutral",
};

function HealthRow({ p, onCheckup, busy }: { p: ProfileHealth; onCheckup: () => void; busy: boolean }) {
  return (
    <div className="flex items-center gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="type-body text-foreground">{p.id}</span>
          <IntentBadge intent={INTENT[p.state]}>{p.state}</IntentBadge>
          {p.refused ? <IntentBadge intent="danger">version refused</IntentBadge> : null}
          {!p.keyPresent ? <IntentBadge intent="warning">no key</IntentBadge> : null}
        </div>
        {p.reasons.length ? (
          <p className="truncate type-subtext text-muted-foreground">{p.reasons.join(" · ")}</p>
        ) : null}
      </div>
      <Button variant="ghost" size="sm" disabled={busy} onClick={onCheckup}>
        {busy ? "Checking…" : "Run checkup"}
      </Button>
    </div>
  );
}

export function HealthSection() {
  const health = useApi(modelsHealth.get, {});
  const [busyId, setBusyId] = useState<string | null>(null);

  async function checkup(provider?: string) {
    setBusyId(provider ?? "*");
    try {
      await apiCall(modelsHealth.checkup, { body: provider ? { provider } : {} });
      await health.mutate();
    } finally {
      setBusyId(null);
    }
  }

  const profiles = health.data?.profiles ?? [];
  return (
    <SettingsSection
      title="Gateway health"
      description="Anthropic-compatible bridge profiles (DeepSeek, Kimi, GLM, Qwen, MiniMax, local Ollama, OpenRouter) and what the provider doctor last found. A quarantined profile never silently degrades the fleet — runs take one deterministic fallback hop."
    >
      {profiles.length === 0 ? (
        <p className="type-body text-muted-foreground">
          {health.data?.cliAvailable === false
            ? "excellent-mcp is not reachable — health state unavailable."
            : "No gateway profiles probed yet. Run a checkup to populate provider-health.json."}
        </p>
      ) : (
        <div>
          {profiles.map((p) => (
            <HealthRow key={p.id} p={p} onCheckup={() => void checkup(p.id)} busy={busyId === p.id} />
          ))}
        </div>
      )}
      <div className="mt-3">
        <Button variant="outline" disabled={busyId !== null} onClick={() => void checkup()}>
          {busyId === "*" ? "Checking all…" : "Run checkup on all profiles"}
        </Button>
      </div>
    </SettingsSection>
  );
}
